'use client';

import {
  createColumnHelper, getCoreRowModel, useReactTable
} from '@tanstack/react-table';
import clsx from 'clsx';
import { DateTime } from 'luxon';
import { highlight, languages } from 'prismjs';
import { useCallback, useMemo, useState } from 'react';
import { FiLoader } from 'react-icons/fi';
import { PiListMagnifyingGlass } from 'react-icons/pi';
import Editor from 'react-simple-code-editor';
import { match } from 'ts-pattern';
import { z } from 'zod';
import 'prismjs/components/prism-json';
import 'prismjs/themes/prism.css';

import APIGateway from '@/api';
import ModalSourceShow from '@/app/evaluations/_modalSourceShow/modalSourceShow';
import { Button } from '@/components/button/button';
import { Icon } from '@/components/icon/icon';
import { Input } from '@/components/input/input';
import { Loader } from '@/components/loader/loader';
import Modal from '@/components/modal/modal';
import { NativeTable } from '@/components/nativeTable/nativeTable';
import { CursorPaginationState } from '@/components/pagination/pagination';
import { buildColumnClassString, ColumnModEnums } from '@/components/table/table';
import { Heading } from '@/components/typography/heading';
import { useSourcesSearch } from '@/hooks/queries/useSources';
import { useCursorPagination } from '@/hooks/useCursorPagination';
import { useModalKeyboard } from '@/hooks/useModalKeyboard';
import {
  RunCreateReq,
  ScheduleCreateReq,
  Source,
  SourceGetRes
} from '@/types/evals';
import { getResourceIcon, getResourceName } from '@/utils/sourceUtils';

import { EXAMPLES_JSON_HELP, EXAMPLES_JSON_LABEL, EXAMPLES_JSON_PLACEHOLDER } from '../_components/examplesJson';
import { exampleMessageSchema } from '../_modalSourceCreate/schemas';
import styles from './modalRunCreate.module.scss';

const PAGE_SIZE = 10;

const INPUT_MODE = {
  SOURCE: 'source',
  JSON: 'json'
} as const;
type InputModeType = typeof INPUT_MODE[keyof typeof INPUT_MODE];

const STEP = {
  CONFIG: 'config',
  SCHEDULE: 'schedule'
} as const;
type StepType = typeof STEP[keyof typeof STEP];

const SCHEDULE_MODE = {
  NOW: 'now',
  RECURRING: 'recurring'
} as const;
type ScheduleModeType = typeof SCHEDULE_MODE[keyof typeof SCHEDULE_MODE];

const REPEAT_UNIT = {
  HOURS: 'hours',
  DAYS: 'days'
} as const;
type RepeatUnitType = typeof REPEAT_UNIT[keyof typeof REPEAT_UNIT];

const REPEAT_INTERVAL_OPTIONS = [ 1, 2, 4, 6, 12, 24 ] as const;

// JSON validation. Reuses the shared exampleMessageSchema (text-or-structured,
// exactly-one) from the source-create schemas so both modals validate messages
// identically. The MESSAGE user/assistant ordering is enforced by the refine
// below.
const ExampleSchema = z.object( {
  id: z.string().optional(),
  type: z.enum( [ 'message', 'conversation' ] ),
  data: z.array( exampleMessageSchema ).min( 1 ),
  meta_data: z.record( z.string(), z.unknown() ).optional()
} ).refine(
  ( example ) => {
    // MESSAGE accepts [user, assistant] (chat) or a single assistant turn
    // (typed-input agents; user input lives in meta_data).
    if ( example.type === 'message' ) {
      if ( example.data.length === 2 ) {
        return example.data[0].user === 'user' && example.data[1].user === 'assistant';
      }
      if ( example.data.length === 1 ) {
        return example.data[0].user === 'assistant';
      }
      return false;
    }
    return true;
  },
  { message: 'Message type must be [user, assistant] or a single assistant turn (typed-input); conversation type needs ≥1 message.' }
);

const JsonExamplesSchema = z.object( {
  examples: z.array( ExampleSchema ).min( 1, 'At least one example is required' )
} );

export const validateJsonString = ( jsonString?: string ): { valid: boolean; error?: string } => {
  if ( !jsonString || jsonString.trim() === '' ) return { valid: false, error: 'JSON data is required' };
  try {
    const parsed = JSON.parse( jsonString );
    const result = JsonExamplesSchema.safeParse( parsed );
    if ( !result.success ) return { valid: false, error: z.prettifyError( result.error ) };
    return { valid: true };
  } catch {
    return { valid: false, error: 'Invalid JSON format' };
  }
};

const columnHelper = createColumnHelper<Source>();

type SourcesTableProps = {
  sources: Source[];
  selectedSourceId?: string;
  reloadedSourceIndex?: number;
  cursorPagination: CursorPaginationState;
  onSelect: ( source_id: string ) => void;
  onVersionChange: ( sourceIndex: number, version: number ) => void;
  onPeek: ( index: number ) => void;
  disabled?: boolean;
};

function SourcesTable( {
  sources, selectedSourceId, reloadedSourceIndex, cursorPagination,
  onSelect, onVersionChange, onPeek, disabled
}: SourcesTableProps ) {
  const columns = useMemo( () => [
    columnHelper.display( {
      id: 'select',
      header: '',
      size: 50,
      cell: info => {
        const source = info.row.original;
        return (
          <input
            type='radio'
            name='run-source-selection'
            checked={ source.id === selectedSourceId }
            disabled={ disabled }
            onChange={ () => onSelect( source.id ) }
            data-testid={ `source-radio-${ source.id }` }
          />
        );
      },
      meta: { classStr: buildColumnClassString( { width: 5, styles: ColumnModEnums.CENTER } ) }
    } ),
    columnHelper.accessor( 'name', {
      header: 'Source Name',
      cell: info => info.getValue(),
      meta: { classStr: buildColumnClassString( { width: 25, styles: ColumnModEnums.NO_OVERFLOW } ) }
    } ),
    columnHelper.accessor( 'version', {
      header: 'Version',
      cell: info => {
        const source = info.row.original;
        const idx = info.row.index;
        const max = source.latest_version_number ?? source.version;
        return (
          <select
            value={ source.version }
            disabled={ disabled || reloadedSourceIndex === idx }
            onChange={ ( e ) => onVersionChange( idx, parseInt( e.target.value ) ) }
          >
            { Array.from( { length: max }, ( _, i ) => i + 1 ).map( v => (
              <option value={ v } key={ `${ source.id }-v-${ v }` }>{ v }</option>
            ) ) }
          </select>
        );
      },
      meta: { classStr: buildColumnClassString( { width: 10, styles: ColumnModEnums.CENTER } ) }
    } ),
    columnHelper.display( {
      id: 'resource',
      header: 'Source',
      cell: info => {
        const source = info.row.original;
        return (
          <div className={ styles.source_resource } title={ getResourceName( source ) }>
            <span>{ getResourceIcon( source ) }</span>
            <span>{ getResourceName( source ) }</span>
            <span>({ source.example_count })</span>
          </div>
        );
      },
      meta: { classStr: buildColumnClassString( { width: 40, styles: ColumnModEnums.NO_OVERFLOW } ) }
    } ),
    columnHelper.accessor( 'example_type', {
      header: 'Type',
      cell: info => info.getValue(),
      meta: { classStr: buildColumnClassString( { width: 15 } ) }
    } ),
    columnHelper.display( {
      id: 'peek',
      header: 'Peek',
      cell: info => {
        const idx = info.row.index;
        return reloadedSourceIndex === idx ? (
          <Icon icon={ FiLoader } />
        ) : (
          <Icon
            icon={ PiListMagnifyingGlass }
            className='table_actions_item'
            style={ { cursor: 'pointer' } }
            onClick={ () => onPeek( idx ) }
          />
        );
      },
      meta: { classStr: buildColumnClassString( { width: 5, styles: ColumnModEnums.CENTER } ) }
    } )
  ], [ selectedSourceId, reloadedSourceIndex, disabled, onSelect, onVersionChange, onPeek ] );

  const table = useReactTable( {
    data: sources,
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableSorting: false
  } );

  return <NativeTable table={ table } cursorPagination={ cursorPagination } />;
}

type Props = {
  evaluationId: string;
  evaluationName: string;
  onCreateRun: ( req: RunCreateReq ) => void;
  onCreateSchedule: ( req: ScheduleCreateReq ) => void;
  onClose: () => void;
  isRunPending?: boolean;
  isRunSuccess?: boolean;
  isRunError?: boolean;
  runErrorMessage?: string;
  isSchedulePending?: boolean;
  isScheduleSuccess?: boolean;
  isScheduleError?: boolean;
  scheduleErrorMessage?: string;
};

const getBrowserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

const buildCronExpression = ( start: DateTime, interval: number, unit: RepeatUnitType ): string => {
  const minute = start.minute;
  const hour = start.hour;
  return match( unit )
    .with( REPEAT_UNIT.HOURS, () => `${ minute } */${ interval } * * *` )
    .with( REPEAT_UNIT.DAYS, () => `${ minute } ${ hour } */${ interval } * *` )
    .exhaustive();
};

const formatCadenceSummary = ( interval: number, unit: RepeatUnitType, start: DateTime, timezone: string ): string => {
  const startStr = start.setZone( timezone ).toFormat( 'yyyy-LL-dd \'at\' HH:mm' );
  const unitLabel = unit === REPEAT_UNIT.HOURS ? ( interval === 1 ? 'hour' : 'hours' ) : ( interval === 1 ? 'day' : 'days' );
  return `Runs every ${ interval } ${ unitLabel } in ${ timezone }, starting ${ startStr }. Stops when the evaluation is deprecated.`;
};

export default function ModalRunCreate( {
  evaluationId, evaluationName,
  onCreateRun, onCreateSchedule, onClose,
  isRunPending, isRunSuccess, isRunError, runErrorMessage,
  isSchedulePending, isScheduleSuccess, isScheduleError, scheduleErrorMessage
}: Props ) {
  // Backend rejections from either submission path render inline above the
  // schedule-step footer (the step the user clicks Run/Create Schedule from).
  // Only one mutation can be in flight at a time, so a single banner that
  // picks whichever error is set is sufficient.
  const submitErrorMessage = isRunError && runErrorMessage
    ? runErrorMessage
    : isScheduleError && scheduleErrorMessage
      ? scheduleErrorMessage
      : undefined;
  // Step
  const [ step, setStep ] = useState<StepType>( STEP.CONFIG );

  // Config step state
  const [ inputMode, setInputMode ] = useState<InputModeType>( INPUT_MODE.SOURCE );
  const [ includeReason, setIncludeReason ] = useState( false );
  const [ selectedSourceId, setSelectedSourceId ] = useState<string | undefined>( undefined );
  const [ jsonData, setJsonData ] = useState<string>( '' );
  const [ jsonValidationError, setJsonValidationError ] = useState<string | undefined>( undefined );
  const [ peekIndex, setPeekIndex ] = useState<number | undefined>( undefined );
  const { cursors, currentCursorIdx, currentCursor: cursor, currentPage, canGoPrev, goNext, goPrev, reset: resetCursor } = useCursorPagination();
  const [ pageSize, setPageSize ] = useState( PAGE_SIZE );
  const [ sourceNameFilter, setSourceNameFilter ] = useState( '' );
  const [ overriddenSources, setOverriddenSources ] = useState<Map<string, Source>>( new Map() );
  const [ reloadedSourceIndex, setReloadedSourceIndex ] = useState<number | undefined>( undefined );

  // Schedule step state
  const [ scheduleMode, setScheduleMode ] = useState<ScheduleModeType>( SCHEDULE_MODE.NOW );
  const browserTz = useMemo( () => getBrowserTimezone(), [] );
  const [ timezone ] = useState<string>( browserTz );
  const [ startsAtLocal, setStartsAtLocal ] = useState<string>( () => DateTime.now().plus( { hours: 1 } ).startOf( 'minute' ).toFormat( 'yyyy-LL-dd\'T\'HH:mm' ) );
  const [ repeatInterval, setRepeatInterval ] = useState<number>( 1 );
  const [ repeatUnit, setRepeatUnit ] = useState<RepeatUnitType>( REPEAT_UNIT.DAYS );

  // Submission summary (used for success view)
  const [ submitted, setSubmitted ] = useState<{
    kind: 'run' | 'schedule';
    summary: string;
  } | undefined>( undefined );

  const isBusy = !!( isRunPending || isSchedulePending );
  const showSuccess = !!( isRunSuccess || isScheduleSuccess ) && submitted;

  useModalKeyboard( { onEscape: () => { if ( !isBusy ) onClose(); } } );

  const { data: sourcesData, isLoading: isLoadingSources, isError: isSourcesError } = useSourcesSearch( {
    limit: pageSize,
    cursor: cursor ?? undefined,
    is_deprecated: false,
    name: sourceNameFilter || undefined,
    sort_by: cursor ? undefined : 'created_at',
    sort_order: cursor ? undefined : 'desc'
  } );

  const displayedSources = useMemo( () => {
    const items = sourcesData?.items ?? [];
    if ( overriddenSources.size === 0 ) return items;
    return items.map( s => overriddenSources.get( s.id ) ?? s );
  }, [ sourcesData?.items, overriddenSources ] );

  const handleNextPage = useCallback( () => {
    if ( sourcesData?.nextCursor ) goNext( sourcesData.nextCursor );
  }, [ sourcesData?.nextCursor, goNext ] );

  const handlePageSizeChange = useCallback( ( size: number ) => {
    setPageSize( size );
    resetCursor();
  }, [ resetCursor ] );

  const onSelectSource = useCallback( ( source_id: string ) => {
    setSelectedSourceId( prev => ( prev === source_id ? undefined : source_id ) );
  }, [] );

  const onVersionChange = useCallback( async( index: number, version: number ) => {
    const original = ( sourcesData?.items ?? [] )[ index ];
    if ( !original ) return;
    try {
      setReloadedSourceIndex( index );
      const fetched: SourceGetRes = await APIGateway.evals.sourceGet( original.id, version );
      setOverriddenSources( prev => {
        const next = new Map( prev );
        next.set( original.id, fetched as Source );
        return next;
      } );
    } finally {
      setReloadedSourceIndex( undefined );
    }
  }, [ sourcesData?.items ] );

  const handleJsonChange = ( code: string ) => {
    setJsonData( code );
    const v = validateJsonString( code );
    setJsonValidationError( v.valid ? undefined : v.error );
  };

  const buildRunCreateReq = (): RunCreateReq | undefined => {
    if ( inputMode === INPUT_MODE.SOURCE ) {
      if ( !selectedSourceId ) return undefined;
      const selected = displayedSources.find( s => s.id === selectedSourceId );
      if ( !selected ) return undefined;
      return {
        data_source: {
          data_origin: 'reference',
          source_id: selected.id,
          source_version: selected.version
        },
        include_reason: includeReason
      };
    }
    const v = validateJsonString( jsonData );
    if ( !v.valid ) return undefined;
    const parsed = JSON.parse( jsonData ) as {
      examples: Array<{
        id?: string;
        type: 'message' | 'conversation';
        data: Array<{ user: 'user' | 'assistant'; message: string }>;
        meta_data?: Record<string, unknown>;
      }>;
    };
    return {
      data_source: {
        data_origin: 'inline',
        examples: parsed.examples.map( e => ( {
          id: e.id,
          type: e.type,
          data: e.data,
          meta_data: e.meta_data
        } ) )
      },
      include_reason: includeReason
    };
  };

  const configIsValid = useMemo( () => {
    if ( inputMode === INPUT_MODE.SOURCE ) return !!selectedSourceId;
    return !!jsonData && !jsonValidationError;
  }, [ inputMode, selectedSourceId, jsonData, jsonValidationError ] );

  const handleNext = () => {
    if ( !configIsValid ) return;
    setStep( STEP.SCHEDULE );
  };

  const handleSubmit = () => {
    const runReq = buildRunCreateReq();
    if ( !runReq ) return;

    if ( scheduleMode === SCHEDULE_MODE.NOW ) {
      setSubmitted( {
        kind: 'run',
        summary: 'This run has started and will occur once. The evaluation is running now using the selected data and metrics.'
      } );
      onCreateRun( runReq );
      return;
    }

    // Recurring schedule — only supported for reference sources (server requires sourceId)
    if ( runReq.data_source.data_origin !== 'reference' ) return;
    const startDt = DateTime.fromISO( startsAtLocal, { zone: timezone } );
    if ( !startDt.isValid ) return;

    const cron = buildCronExpression( startDt, repeatInterval, repeatUnit );
    const scheduleReq: ScheduleCreateReq = {
      evaluation_id: evaluationId,
      source_id: runReq.data_source.source_id,
      source_version: typeof runReq.data_source.source_version === 'number' ? runReq.data_source.source_version : undefined,
      cron_expression: cron,
      timezone
    };

    setSubmitted( {
      kind: 'schedule',
      summary: formatCadenceSummary( repeatInterval, repeatUnit, startDt, timezone )
    } );
    onCreateSchedule( scheduleReq );
  };

  const canScheduleRecurring = inputMode === INPUT_MODE.SOURCE;

  if ( showSuccess && submitted ) {
    return (
      <Modal
        className={ styles.modal }
        resizable
        modalType="run-create"
        initialSize="medium"
        minSize={ { width: 640, height: 400 } }
      >
        <div className={ styles.form } data-testid="run-create-success">
          <div className={ styles.header }>
            <Heading as="h2" size="md">
              { submitted.kind === 'run' ? 'Evaluation run started' : 'Evaluation Schedule created' }
            </Heading>
            <span className={ styles.subheader }>
              { submitted.kind === 'run' ? 'This run has started and will occur once.' : 'A recurring evaluation schedule has been created.' }
            </span>
          </div>
          <div className={ styles.success_summary }>{ submitted.summary }</div>
          <div className={ styles.footer }>
            <Button variant="secondary" size="sm" type="button" onClick={ onClose } data-testid="run-success-close-button">
              Close
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      className={ styles.modal }
      resizable
      modalType="run-create"
      initialSize="large"
      minSize={ { width: 720, height: 600 } }
    >
      { peekIndex !== undefined && displayedSources[ peekIndex ] && (
        <ModalSourceShow
          source={ displayedSources[ peekIndex ] }
          onCloseAction={ () => setPeekIndex( undefined ) }
        />
      ) }

      <div className={ styles.form }>
        { step === STEP.CONFIG ? (
          <>
            <div className={ styles.header }>
              <Heading as="h2" size="md">{ `Create Evaluation Run for ${ evaluationName }` }</Heading>
              <span className={ styles.subheader }>
                Run this evaluation to apply its metrics to selected data and generate results.
              </span>
            </div>

            <div className={ styles.field }>
              <label className={ styles.toggle_row }>
                <input
                  type="checkbox"
                  checked={ includeReason }
                  onChange={ ( e ) => setIncludeReason( e.target.checked ) }
                  disabled={ isBusy }
                  data-testid="include-reason-toggle"
                />
                <span>
                  <span className={ styles.field_label }>Display Evaluation Reasoning in Results?</span>
                  <span className={ styles.field_help }> &nbsp;Model&apos;s reasoning for assigned run scores will be shown.</span>
                </span>
              </label>
            </div>

            <div className={ styles.field }>
              <span className={ styles.field_label }>Select Examples Input Mode</span>
              <div className={ styles.mode_tabs } data-testid="input-mode-toggle">
                <Button
                  variant="secondary"
                  size="sm"
                  type="button"
                  className={ inputMode === INPUT_MODE.SOURCE ? styles.active : '' }
                  onClick={ () => setInputMode( INPUT_MODE.SOURCE ) }
                  data-testid="mode-source-button"
                  disabled={ isBusy }
                >Select a Source
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  type="button"
                  className={ inputMode === INPUT_MODE.JSON ? styles.active : '' }
                  onClick={ () => setInputMode( INPUT_MODE.JSON ) }
                  data-testid="mode-json-button"
                  disabled={ isBusy }
                >Provide JSON
                </Button>
              </div>
              <span className={ styles.field_help }>
                Choose data from an existing source (e.g., Snowflake or agent logs), or provide inline JSON just for this run.
              </span>
            </div>

            { inputMode === INPUT_MODE.SOURCE ? (
              <div className={ styles.sources_section }>
                <div className={ styles.sources_header }>
                  <div className={ styles.sources_intro }>
                    <span className={ styles.sources_intro_title }>Select a source</span>
                    <span className={ styles.sources_intro_help }>
                      Don&apos;t see a source? You may not have Owner access to use that source in evaluations.
                    </span>
                  </div>
                  <Input
                    id="source-name-filter"
                    type="text"
                    label="Filter by source name"
                    placeholder="Filter by source name..."
                    value={ sourceNameFilter }
                    onChange={ ( e ) => {
                      setSourceNameFilter( e.target.value );
                      resetCursor();
                    } }
                    disabled={ isBusy }
                  />
                </div>
                { isLoadingSources ? (
                  <Loader />
                ) : isSourcesError ? (
                  <div className={ styles.error_message }>Error loading sources</div>
                ) : (
                  <SourcesTable
                    sources={ displayedSources }
                    selectedSourceId={ selectedSourceId }
                    reloadedSourceIndex={ reloadedSourceIndex }
                    cursorPagination={ {
                      hasMore: sourcesData?.hasMore ?? false,
                      canGoPrev,
                      currentPage,
                      pageSize,
                      onNextPage: handleNextPage,
                      onPrevPage: goPrev,
                      onPageSizeChange: handlePageSizeChange
                    } }
                    onSelect={ onSelectSource }
                    onVersionChange={ onVersionChange }
                    onPeek={ ( i ) => setPeekIndex( i ) }
                    disabled={ isBusy }
                  />
                ) }
              </div>
            ) : (
              <div className={ styles.field }>
                <label htmlFor="json-input" className={ styles.field_label }>
                  { EXAMPLES_JSON_LABEL } <span className={ styles.required_asterisk }>*</span>
                </label>
                <p className={ styles.field_help }>{ EXAMPLES_JSON_HELP }</p>
                <div data-testid="json-data-input">
                  <Editor
                    value={ jsonData }
                    onValueChange={ handleJsonChange }
                    highlight={ ( code ) => highlight( code, languages.json, 'json' ) }
                    padding={ 10 }
                    className={ styles.json_editor }
                    placeholder={ EXAMPLES_JSON_PLACEHOLDER }
                  />
                </div>
                { jsonValidationError && (
                  <span className={ styles.error_message } data-testid="json-validation-error">{ jsonValidationError }</span>
                ) }
              </div>
            ) }

            <div className={ styles.footer }>
              { !configIsValid && (
                <span className={ styles.footer_hint }>Select a source or provide JSON to create a run.</span>
              ) }
              <Button variant="secondary" size="sm" type="button" onClick={ onClose } disabled={ isBusy }>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                type="button"
                onClick={ handleNext }
                disabled={ !configIsValid || isBusy }
                data-testid="run-create-next-button"
              >Next
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className={ styles.header }>
              <Heading as="h2" size="md">{ `Evaluation Schedule for ${ evaluationName }` }</Heading>
              <span className={ styles.subheader }>When should this evaluation run?</span>
            </div>

            <div className={ styles.schedule_radios }>
              <label className={ clsx( styles.schedule_radio, scheduleMode === SCHEDULE_MODE.NOW && styles.selected ) }>
                <input
                  type="radio"
                  name="schedule-mode"
                  checked={ scheduleMode === SCHEDULE_MODE.NOW }
                  onChange={ () => setScheduleMode( SCHEDULE_MODE.NOW ) }
                  disabled={ isBusy }
                  data-testid="schedule-mode-now"
                />
                <span className={ styles.radio_body }>
                  <span className={ styles.radio_title }>Run now (one time)</span>
                  <span className={ styles.radio_help }>Runs immediately and does not repeat.</span>
                </span>
              </label>

              <label
                className={ clsx(
                  styles.schedule_radio,
                  scheduleMode === SCHEDULE_MODE.RECURRING && styles.selected,
                  !canScheduleRecurring && styles.error_message
                ) }
                title={ !canScheduleRecurring ? 'Recurring schedules require a saved source (not inline JSON).' : undefined }
              >
                <input
                  type="radio"
                  name="schedule-mode"
                  checked={ scheduleMode === SCHEDULE_MODE.RECURRING }
                  onChange={ () => setScheduleMode( SCHEDULE_MODE.RECURRING ) }
                  disabled={ isBusy || !canScheduleRecurring }
                  data-testid="schedule-mode-recurring"
                />
                <span className={ styles.radio_body }>
                  <span className={ styles.radio_title }>Run on a schedule</span>
                  <span className={ styles.radio_help }>
                    { canScheduleRecurring
                      ? 'Runs automatically on a recurring cadence.'
                      : 'Recurring schedules require a saved source (not inline JSON).'}
                  </span>
                </span>
              </label>
            </div>

            { scheduleMode === SCHEDULE_MODE.RECURRING && canScheduleRecurring && (
              <div className={ styles.schedule_config }>
                <div className={ styles.schedule_row }>
                  <Input
                    id="schedule-start-input"
                    type="datetime-local"
                    label="Starting on"
                    value={ startsAtLocal }
                    onChange={ ( e ) => setStartsAtLocal( e.target.value ) }
                    disabled={ isBusy }
                    data-testid="schedule-start-input"
                  />
                  <span className={ styles.field_help }>({ timezone })</span>
                </div>
                <div className={ styles.schedule_row }>
                  <span className={ styles.schedule_row_label }>Repeat every</span>
                  <select
                    className={ styles.schedule_inline_select }
                    value={ repeatInterval }
                    onChange={ ( e ) => setRepeatInterval( parseInt( e.target.value ) ) }
                    disabled={ isBusy }
                    data-testid="schedule-interval-select"
                  >
                    { REPEAT_INTERVAL_OPTIONS.map( v => (
                      <option key={ `interval-${ v }` } value={ v }>{ v }</option>
                    ) ) }
                  </select>
                  <select
                    className={ styles.schedule_inline_select }
                    value={ repeatUnit }
                    onChange={ ( e ) => setRepeatUnit( e.target.value as RepeatUnitType ) }
                    disabled={ isBusy }
                    data-testid="schedule-unit-select"
                  >
                    <option value={ REPEAT_UNIT.HOURS }>Hours</option>
                    <option value={ REPEAT_UNIT.DAYS }>Days</option>
                  </select>
                </div>
                <div className={ styles.schedule_row }>
                  <span className={ styles.schedule_row_label }>Ends</span>
                  <span className={ styles.field_help }>Never &mdash; stops when the evaluation is deprecated.</span>
                </div>
              </div>
            ) }

            { submitErrorMessage && (
              <div className='layout__error' style={ { marginTop: '8px' } } data-testid='run-create-error'>
                { submitErrorMessage }
              </div>
            ) }

            <div className={ styles.footer }>
              <Button variant="secondary" size="sm" type="button" onClick={ () => setStep( STEP.CONFIG ) } disabled={ isBusy }>
                Back
              </Button>
              <Button variant="secondary" size="sm" type="button" onClick={ onClose } disabled={ isBusy }>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                type="button"
                onClick={ handleSubmit }
                loading={ isBusy }
                disabled={ isBusy }
                data-testid="run-create-submit-button"
              >
                { scheduleMode === SCHEDULE_MODE.NOW ? 'Create Run' : 'Create Schedule' }
              </Button>
            </div>
          </>
        ) }
      </div>
    </Modal>
  );
}
